/*
 *                     NOTICE
 *            COPYRIGHT 2024 Ab Initio
 *       UNPUBLISHED - ALL RIGHTS RESERVED
 *
 * This program is confidential and a trade secret of Ab Initio.
 * The receipt or possession of this program does not convey any
 * rights to reproduce or disclose its contents, or to manufacture,
 * use or sell anything that it may describe, in whole or in part,
 * without the specific written consent of Ab Initio.
 */
import { Expression } from "abreact/expression/Expression";
import { AbstractStandardOperandExpressionNodeConverter } from "abreact/expression/ui/editor/impl/model/impl/row/operandValue/converter/AbstractStandardOperandExpressionNodeConverter";
import { AbstractStandardOperandMultiplexExpressionNodeConverter } from "abreact/expression/ui/editor/impl/model/impl/row/operandValue/converter/multiplex/AbstractStandardOperandMultiplexExpressionNodeConverter";
import { OperatorOperandValueImpl } from "abreact/expression/ui/editor/impl/model/impl/row/operandValue/OperatorOperandValueImpl";
import { AbImmutableRecord } from "abreact/utils/AbImmutableRecord";
import { ExpExpressionNodeNodeTypeEnum } from "abweb/generated/abwebinternal/api/dto/ExpExpressionNode";
import { Assert } from "abweb/utils/Assert";
import { NullUtils } from "abweb/utils/NullUtils";
import { ObjectUtils } from "abweb/utils/ObjectUtils";
import Immutable from "immutable";
// import CATEGORY_FORMULA = Expression.Domain.Constants.CATEGORY_FORMULA;
// import CATEGORY_SUB_EXPRESSION = Expression.Domain.Constants.CATEGORY_SUB_EXPRESSION;
// import ROLE_ABSENT = Expression.Domain.Constants.ROLE_ABSENT;
// import ROLE_ACTION = Expression.Domain.Constants.ROLE_ACTION;
// import ROLE_CONDITION = Expression.Domain.Constants.ROLE_CONDITION;
// import ROLE_FORMULA = Expression.Domain.Constants.ROLE_FORMULA;
// import ROLE_FUNCTION = Expression.Domain.Constants.ROLE_FUNCTION;
// import ROLE_JOIN = Expression.Domain.Constants.ROLE_JOIN;
// import ValidatedExpOperator = Expression.Domain.ValidatedExpOperator;
// import WithoutNodeType = GuidedExpressionEditor.AbstractModel.AbstractRowModel.AbstractOperandValue.WithoutNodeType;
// import OperandValue = GuidedExpressionEditor.AbstractModel.AbstractRowModel.OperandValue;
// import OperatorOperandValue = GuidedExpressionEditor.AbstractModel.AbstractRowModel.OperatorOperandValue;

export class OperatorOperandExpressionNodeConverter extends AbstractStandardOperandExpressionNodeConverter {
  convert(expExpressionNode) {
    Assert.assert(expExpressionNode.nodeType === ExpExpressionNodeNodeTypeEnum.OPERATOR);
    const stateWithoutNodeType = this.getStateWithoutNodeType(expExpressionNode);
    let operatorOperandValue = new OperatorOperandValueImpl(stateWithoutNodeType);
    if (this.specification.summaryGenerator != null && this.specification.expTemplateConfiguration != null) {
      const displayValue = this.specification.summaryGenerator.createStringSummary(operatorOperandValue, Expression.Domain.Constants.FUNCTION_SUMMARY_STANDARD, true, this.specification.expressionDomainModel);
      operatorOperandValue = operatorOperandValue.withDisplayValue(displayValue);
    }
    return operatorOperandValue;
  }
  getStateWithoutNodeType(expExpressionNode) {
    const {
      expressionDomainModel,
      containingExpOperator
    } = this.specification;
    const expOperator = this.resolveExpOperator(expExpressionNode, expressionDomainModel, containingExpOperator);
    this.validateExpExpressionNode(expExpressionNode, expOperator);
    const childExpExpressionNodes = expExpressionNode.operandValues != null ? [...expExpressionNode.operandValues] : [];
    const sourceOperandValue = ObjectUtils.createMutable();
    const executionFlags = [];
    const argumentOperandValuesByOperandName = new Map();
    switch (expOperator.roleID) {
      case Expression.Domain.Constants.ROLE_JOIN:
        if (childExpExpressionNodes.length > 0) {
          this.processJoiningOperatorChildExpExpressionNodes(childExpExpressionNodes, expOperator, argumentOperandValuesByOperandName);
        }
        break;
      case Expression.Domain.Constants.ROLE_CONDITION:
      case Expression.Domain.Constants.ROLE_ACTION:
      case Expression.Domain.Constants.ROLE_ABSENT:
      case Expression.Domain.Constants.ROLE_FUNCTION:
      case Expression.Domain.Constants.ROLE_FORMULA:
        if (childExpExpressionNodes.length > 0) {
          this.processStandardOperatorChildExpExpressionNodes(childExpExpressionNodes, expOperator, sourceOperandValue, argumentOperandValuesByOperandName);
        }
        if (expExpressionNode.executionFlags != null) {
          executionFlags.push(...expExpressionNode.executionFlags);
        }
        break;
      default:
        Assert.unexpected(`Unrecognized roleID on operator ${expOperator.id}: ${expOperator.roleID}`);
    }
    return {
      ...super.getStateWithoutNodeType(expExpressionNode),
      valueCategoryID: expExpressionNode.categoryID ?? expOperator.output.categoryID,
      valueTypeIDs: Immutable.List.of(expOperator.output.valueTypeID),
      multiple: expOperator.output.multiple ?? false,
      typeQualifier: expOperator.output.outputQualifier,
      argumentOperandValuesByOperandName: Immutable.Map(argumentOperandValuesByOperandName),
      operandName: expExpressionNode.operandName,
      displayValue: expExpressionNode.displayValue,
      sourceOperandValue: sourceOperandValue.value,
      expOperatorObject: AbImmutableRecord.State.Object.from(expOperator),
      isPlaceholder: expressionDomainModel.isPlaceholderExpOperator(expOperator),
      executionFlags: Immutable.Set(executionFlags)
    };
  }

  /**
   * If {@link expExpressionNode#operatorID} is undefined, {@link expExpressionNode} must represent a
   * {@link Expression.Domain.Constants.ROLE_CONDITION} or {@link Expression.Domain.Constants.ROLE_ACTION} operator, effectively either type of row, for whom only a source
   * operand value has been selected. Please see the associated comments in {@link RowBasedRowModelImpl#constructor} and
   * {@link RowBasedRowModelImpl#export}.
   */
  resolveExpOperator(expExpressionNode, expressionDomainModel, containingExpOperator) {
    let expOperator;
    if (expExpressionNode.operatorID == null) {
      Assert.assert(containingExpOperator == null || Expression.Domain.OperatorRole.isJoin(containingExpOperator));
      expOperator = expressionDomainModel.absentSentinelExpOperator;
      Assert.assert(Expression.Domain.OperatorRole.isAbsent(expOperator));
    } else {
      expOperator = expressionDomainModel.getExpOperator(expExpressionNode.operatorID);
      Assert.assert(!Expression.Domain.OperatorRole.isAbsent(expOperator));
    }
    return expOperator;
  }
  validateExpExpressionNode(expExpressionNode, expOperator) {
    const {
      output
    } = expOperator;
    if (expExpressionNode.categoryID != null && expExpressionNode.categoryID !== output.categoryID) {
      Assert.assert([Expression.Domain.Constants.CATEGORY_FORMULA, Expression.Domain.Constants.CATEGORY_SUB_EXPRESSION].includes(expExpressionNode.categoryID));
    }
    if (expExpressionNode.valueTypeIDs != null) {
      Assert.assert(expExpressionNode.valueTypeIDs.length === 1);
      Assert.assert(expExpressionNode.valueTypeIDs[0] === output.valueTypeID);
    }
    if (expExpressionNode.typeQualifier != null) {
      Assert.assert(expExpressionNode.typeQualifier === output.outputQualifier);
    }
  }
  processJoiningOperatorChildExpExpressionNodes(childExpExpressionNodes, expOperator, argumentOperandValuesByOperandName) {
    const converter = new OperatorOperandExpressionNodeConverter(this.getChildSpecification(expOperator));
    for (const childExpExpressionNode of childExpExpressionNodes) {
      // Join operators allow multiple child operands with the same operand name, so we use the node id's instead.
      // Join operators are handled by the framework only, so this is not exposed to UI developers using the GEE.
      argumentOperandValuesByOperandName.set(childExpExpressionNode.id, converter.convert(childExpExpressionNode));
    }
  }
  processStandardOperatorChildExpExpressionNodes(childExpExpressionNodes, expOperator, sourceOperandValue, argumentOperandValuesByOperandName) {
    const childSpecification = this.getChildSpecification(expOperator);
    if (expOperator.sourceOperandTypes != null && expOperator.sourceOperandTypes.length) {
      const childExpExpressionNode = childExpExpressionNodes.shift();
      Assert.assert(childExpExpressionNode != null);
      const sourceOperandName = childSpecification.expressionDomainModel.getSourceOperandName(expOperator);
      Assert.assert(childExpExpressionNode.operandName === sourceOperandName);
      sourceOperandValue.value = new AbstractStandardOperandMultiplexExpressionNodeConverter(childSpecification).convert(childExpExpressionNode);
    }
    const {
      expTemplateConfiguration,
      ...remainingSpecification
    } = childSpecification;
    const converter = new AbstractStandardOperandMultiplexExpressionNodeConverter(remainingSpecification);
    for (const childExpExpressionNode of childExpExpressionNodes) {
      const operandName = NullUtils.getRequiredPropertyValue(childExpExpressionNode, "operandName");
      argumentOperandValuesByOperandName.set(operandName, converter.convert(childExpExpressionNode));
    }
  }
  getChildSpecification(expOperator) {
    return {
      ...this.specification,
      containingExpOperator: expOperator
    };
  }
}
//# sourceMappingURL=OperatorOperandExpressionNodeConverter.js.map
